import type { Me } from './me'

// Who was last signed in on this phone, kept so Roundly can open with no signal
// (docs/plans/offline/PLAN-v2.md §2; Liam 1 Oct 2026: "app can't fail offline", and up to 3 days since the phone
// last had signal, accepting that a removed worker's phone can open for that long with only what it already holds).
//
// One record per phone: the LAST person whose /api/me answered online. It is only ever used to open Roundly in
// local-only mode (api.ts: nothing sent, nothing asked of the server) and is replaced by the first online answer.
// A log-out writes a mark that blocks any restore, and any quiet re-entry from a cookie still on the phone, until
// the server has signed the phone out too.

const RECORD_KEY = 'roundly:offline-identity:v1'
const LOGOUT_KEY = 'roundly:signed-out-mark:v1'
export const OFFLINE_LIMIT_MS = 3 * 24 * 60 * 60 * 1000

interface OfflineRecord {
  v: 1
  at: number // when the server last confirmed this person
  me: Me
}

// Only what the screens need to open as this person: role, business and its settings, worker name and prices
// rule, the round's clock and money. Nothing the server would have to re-check is trusted from it beyond that.
function slim(me: Me): Me {
  return {
    role: me.role,
    email: me.email,
    workerName: me.workerName ?? null,
    businessId: me.businessId ?? null,
    businessName: me.businessName ?? null,
    business: me.business ?? null,
    settings: me.settings ?? null,
    avatar: me.avatar ?? null,
    hasOwnPhoto: me.hasOwnPhoto,
    showPrices: me.showPrices,
    trade: me.trade,
    round: me.round,
    // What the screens are drawn with (several jobs, money brought over, working alone): kept, so a phone opened with no
    // signal draws and sends as it did with signal (the final team, 19 Oct 2026: several jobs was dropped here).
    ...(me.severalJobs ? { severalJobs: true } : {}),
    ...(me.broughtMoney ? { broughtMoney: true } : {}),
    ...(me.worksAlone ? { worksAlone: true } : {}),
    // Numbers that asked to stop WhatsApps (0081): kept, so the phone opened with no signal opens no WhatsApp for them.
    ...(Array.isArray(me.waStopped) && me.waStopped.length ? { waStopped: me.waStopped } : {}),
    ...(me.rounds && me.rounds.length > 1 ? { rounds: me.rounds, roundColour: me.roundColour } : {}),
  }
}

export function saveOfflineIdentity(me: Me, now = Date.now()): void {
  if ((me.role !== 'owner' && me.role !== 'worker') || !me.email || me.suspended) return
  try {
    localStorage.setItem(RECORD_KEY, JSON.stringify({ v: 1, at: now, me: slim(me) } satisfies OfflineRecord))
  } catch {
    // Storage full or off: Roundly simply can't open with no signal on this phone.
  }
}

// The person to open as with no signal, or null (none kept, too old, signed out since, or unreadable).
export function readOfflineIdentity(now = Date.now()): { me: Me; at: number } | null {
  if (hasLogoutMark()) return null
  try {
    const raw = localStorage.getItem(RECORD_KEY)
    if (!raw) return null
    const r = JSON.parse(raw) as Partial<OfflineRecord> | null
    if (!r || r.v !== 1 || typeof r.at !== 'number' || !r.me || typeof r.me !== 'object') return null
    if (r.me.role !== 'owner' && r.me.role !== 'worker') return null
    if (typeof r.me.email !== 'string' || !r.me.email) return null
    if (now - r.at > OFFLINE_LIMIT_MS || r.at > now + 60_000) return null
    return { me: r.me as Me, at: r.at }
  } catch {
    return null
  }
}

export function forgetOfflineIdentity(): void {
  try {
    localStorage.removeItem(RECORD_KEY)
  } catch {
    // nothing kept
  }
}

// ---- log-out mark -------------------------------------------------------------------------------------------
// Set the moment someone taps Log out, signal or not, and kept across restarts. While it is there the phone opens
// to sign-in only: no restore, and /api/me is not trusted to quietly sign the phone back in from a cookie the
// server has not yet been told to forget. Cleared once the server sign-out goes through, or a sign-in completes.

export function setLogoutMark(): void {
  try {
    localStorage.setItem(LOGOUT_KEY, String(Date.now()))
  } catch {
    // best effort: the record itself is removed below regardless
  }
  forgetOfflineIdentity()
}

export function hasLogoutMark(): boolean {
  try {
    return localStorage.getItem(LOGOUT_KEY) !== null
  } catch {
    return false
  }
}

export function clearLogoutMark(): void {
  try {
    localStorage.removeItem(LOGOUT_KEY)
  } catch {
    // nothing to clear
  }
}

// ---- this phone's id ----------------------------------------------------------------------------------------
// A random id this phone keeps, sent with "these days are stored here" (POST /api/offline/ack, migration 0038) so
// the owner's diary can tell one phone from another ("If he uses two phones, list both"). It names the phone, not
// the person, so it stays across sign-ins. With storage off, a new one each time Roundly opens.
const DEVICE_KEY = 'roundly:device-id:v1'
let sessionDevice: string | null = null

export function deviceId(): string {
  try {
    const kept = localStorage.getItem(DEVICE_KEY)
    if (kept && /^[A-Za-z0-9-]{8,64}$/.test(kept)) return kept
    const made = crypto.randomUUID()
    localStorage.setItem(DEVICE_KEY, made)
    return made
  } catch {
    sessionDevice ??= crypto.randomUUID()
    return sessionDevice
  }
}
